/**
 * Pure time-overlap matching between a finished workout and the recorded
 * sessions the health store returned. UI-free so it runs under the app's
 * `test:unit` project (Node env, no React Native imports) — this is the logic
 * the #29 acceptance criteria require to be extracted and unit-tested.
 *
 * A workout is matched to the health-store session(s) whose time interval
 * intersects the workout's start→finish window. Zero-width touches (a session
 * that ends exactly when the workout starts) do not count as an overlap.
 */
import type { RecordedSessionSample } from '../health-provider/health-provider.types';

/** The finished workout's time window to match against. */
export interface WorkoutWindow {
  startedAt: Date;
  finishedAt: Date;
}

/** A candidate session with how long it overlapped the workout window. */
export interface SessionCandidate {
  session: RecordedSessionSample;
  /** Overlap with the workout window, in seconds (always > 0). */
  overlapSec: number;
}

export type MatchStatus = 'none' | 'single' | 'multiple';

export interface MatchResult {
  status: MatchStatus;
  /** Overlapping candidates, best (largest overlap) first. Empty for `none`. */
  candidates: SessionCandidate[];
}

export class SessionMatcher {
  /**
   * Classify how the recorded sessions line up with the workout window:
   * `none` (attach nothing), `single` (confirm one), or `multiple` (let the
   * user pick). Candidates are returned sorted by overlap, largest first, so
   * the caller can pre-select the strongest match.
   */
  match(
    window: WorkoutWindow,
    candidates: RecordedSessionSample[],
  ): MatchResult {
    const windowStart = window.startedAt.getTime();
    const windowEnd = window.finishedAt.getTime();

    if (!(windowEnd > windowStart)) {
      return { status: 'none', candidates: [] };
    }

    const overlapping = candidates
      .map((session) => ({
        session,
        overlapSec: this.overlapSeconds(session, windowStart, windowEnd),
      }))
      .filter((candidate) => candidate.overlapSec > 0)
      .sort((a, b) => b.overlapSec - a.overlapSec);

    return {
      status: this.classify(overlapping.length),
      candidates: overlapping,
    };
  }

  private overlapSeconds(
    session: RecordedSessionSample,
    windowStart: number,
    windowEnd: number,
  ): number {
    const start = Math.max(session.startedAt.getTime(), windowStart);
    const end = Math.min(session.endedAt.getTime(), windowEnd);
    return Math.max(0, (end - start) / 1000);
  }

  private classify(count: number): MatchStatus {
    if (count === 0) return 'none';
    if (count === 1) return 'single';
    return 'multiple';
  }
}
